/**
 * `ChurnRiskDrillDown` — per-account drill-down behind the
 * `ChurnRiskCard` headline (ADR-0140 honest-metrics follow-up,
 * 2026-06-23).
 *
 * Fetches `GET /internal/admin/metrics/churn-risk` on open and
 * renders the list of accounts that qualify as `active_accounts_30d`
 * (≥1 real audit_record in the last 30 days) but have recorded
 * NO real activity in the last 14 days — the at-risk band, sorted
 * longest-silent first by the server.
 *
 * Layout:
 *   - Full-screen-ish overlay on mobile (`<sm`): the panel fills the
 *     viewport edge-to-edge with a vertical scroll inside; tap targets
 *     ≥40px; rows stack as cards.
 *   - Centered modal on tablet/desktop (`sm+`): the panel is a
 *     bounded card with an internal scroll region; rows render as a
 *     compact table-ish layout.
 *
 * Header carries the amber churn-risk theme (matches the card the
 * staff clicked through from) and surfaces the server-pegged window
 * dates in ET so the staff sees exactly what "the last 30 / 14 days"
 * resolved to. Footer holds the "Close" action.
 *
 * Per-row outreach affordances:
 *   - `owner_email` as a `mailto:` link (the primary outreach action).
 *     Shows "—" when null and stays non-interactive.
 *   - "Open account" button that closes the drill-down and routes to
 *     the account-detail view via the existing `setView('account-detail',
 *     { accountId })` mechanism (same path AccountsListView uses).
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` on the
 *     panel; Escape closes; backdrop click closes (NOT on mobile —
 *     the panel covers the viewport, there is no backdrop to click).
 *   - Initial focus moves to the close button on open.
 *   - Body scroll is locked while open so the page underneath doesn't
 *     drift on mobile.
 */

import { ChevronRight, ExternalLink, Mail, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { AdminChurnRiskAccount, AdminChurnRiskResponse } from '@sill/types';

import { useAdminApi } from '../../../api/api-context.js';
import { ApiError } from '../../../api/http-client.js';
import type { SetView } from '../../../app/view-router.js';
import { Pill, statusTone } from '../../../components/atoms/Pill.js';
import { formatCount, formatIsoCompact, formatLastActive } from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: AdminChurnRiskResponse };

export interface ChurnRiskDrillDownProps {
  open: boolean;
  onClose: () => void;
  setView: SetView;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export function ChurnRiskDrillDown({
  open,
  onClose,
  setView,
}: ChurnRiskDrillDownProps): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Fetch on open; reset on close so a re-open re-fetches fresh data
  // (the at-risk list changes hour to hour as the silent-band edge
  // sweeps across active accounts).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    (async (): Promise<void> => {
      try {
        const res = await api.getChurnRisk();
        if (cancelled) return;
        setState({ kind: 'ready', data: res });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        setState({ kind: 'error', message: errorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open]);

  // Escape closes; body scroll lock while open so the page underneath
  // doesn't drift on mobile when the panel scrolls.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus to the close button so keyboard users land somewhere
    // sensible; the panel itself isn't a tabbable surface.
    closeBtnRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOpenAccount = (accountId: string): void => {
    // Match AccountsListView.handleRowClick — same view-router entry
    // point so the detail surface receives the account_id the same way.
    onClose();
    setView('account-detail', { accountId });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="churn-risk-drilldown-title"
      className="fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-6"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => {
        // Backdrop click closes (desktop only — on mobile the panel
        // covers the viewport, there's nothing to click around it).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col"
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          // Mobile: full viewport, no rounded corners. Desktop: bounded
          // card with a 3px radius. We pin via inline styles because
          // the responsive shape mixes `position`, `inset`, `height`,
          // and `maxHeight` keys that Tailwind would force into
          // multiple class layers; one block here is clearer.
          position: 'absolute',
          inset: 0,
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0,
        }}
      >
        {/* Inline media-query escape: on sm+ we override the absolute
            fill with a bounded card. CSS-in-JS doesn't get media
            queries directly, so we drop a scoped <style> here — it's
            the same pattern the dashboard uses for the right drawer. */}
        <style>{`
          @media (min-width: 640px) {
            [data-churn-risk-panel] {
              position: relative !important;
              inset: auto !important;
              width: 100% !important;
              max-width: 880px !important;
              max-height: calc(100vh - 48px) !important;
              border-radius: 3px !important;
            }
          }
        `}</style>
        <div data-churn-risk-panel className="flex flex-col" style={{ height: '100%' }}>
          <DrillDownHeader
            onClose={onClose}
            closeBtnRef={closeBtnRef}
            data={state.kind === 'ready' ? state.data : null}
          />
          <DrillDownBody state={state} onOpenAccount={handleOpenAccount} />
          <DrillDownFooter state={state} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── header ───────────────────────── */

function DrillDownHeader({
  onClose,
  closeBtnRef,
  data,
}: {
  onClose: () => void;
  closeBtnRef: React.RefObject<HTMLButtonElement>;
  data: AdminChurnRiskResponse | null;
}): JSX.Element {
  const C = useC();
  return (
    <div
      className="px-4 sm:px-6 py-4 sm:py-5"
      style={{
        // Amber tint carries the churn-risk theme up from the card the
        // staff clicked through from — keeps the visual thread.
        background: C.amberDim,
        borderBottom: `1px solid rgba(255,182,39,0.3)`,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.amberFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Churn risk — drill-down
          </div>
          <h2
            id="churn-risk-drilldown-title"
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(22px, 4.5vw, 30px)',
              lineHeight: 1.1,
              color: C.text,
              letterSpacing: -0.8,
              margin: 0,
            }}
          >
            Churn risk — accounts to re-engage
          </h2>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.body,
              lineHeight: 1.5,
              marginTop: 8,
              maxWidth: 640,
            }}
          >
            Active in the last 30 days but no real traffic in the last 14 —
            sorted by longest silent. Reach out before they go dormant.
          </div>
          {data !== null ? (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 0.5,
                marginTop: 8,
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}
            >
              Active window: {formatIsoCompact(data.active_window_start)} →{' '}
              {formatIsoCompact(data.now)}
              <br className="sm:hidden" />
              <span className="hidden sm:inline"> · </span>
              Silent since: {formatIsoCompact(data.silent_window_start)}
            </div>
          ) : null}
        </div>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Close churn-risk drill-down"
          className="flex items-center justify-center flex-shrink-0"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            color: C.text,
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── body ───────────────────────── */

function DrillDownBody({
  state,
  onOpenAccount,
}: {
  state: FetchState;
  onOpenAccount: (accountId: string) => void;
}): JSX.Element {
  const C = useC();

  if (state.kind === 'loading') {
    return (
      <div
        className="flex-1 flex items-center justify-center px-4 py-12"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          letterSpacing: 1.5,
        }}
      >
        Loading at-risk accounts…
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="flex-1 px-4 sm:px-6 py-8">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Load failed
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
          {state.message}
        </div>
      </div>
    );
  }

  const { accounts } = state.data;

  if (accounts.length === 0) {
    return (
      <div
        className="flex-1 flex items-center justify-center px-4 py-12 text-center"
        style={{
          fontFamily: sansFont,
          fontSize: 14,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        No accounts are cooling off right now.
      </div>
    );
  }

  return (
    <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
      {/* Mobile: stacked cards */}
      <div className="sm:hidden flex flex-col" style={{ gap: 8, padding: 12 }}>
        {accounts.map((row) => (
          <ChurnRiskMobileCard
            key={row.account_id}
            row={row}
            onOpenAccount={onOpenAccount}
          />
        ))}
      </div>
      {/* Desktop: table-ish layout */}
      <div className="hidden sm:block">
        <table
          style={{
            borderCollapse: 'collapse',
            fontFamily: sansFont,
            width: '100%',
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: `1px solid ${C.border}`,
                position: 'sticky',
                top: 0,
                background: C.surface,
                zIndex: 1,
              }}
            >
              <th
                className="text-left px-4 py-3"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  width: '28%',
                }}
              >
                Account
              </th>
              <th
                className="text-left px-4 py-3"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  width: '28%',
                }}
              >
                Owner
              </th>
              <th
                className="text-left px-4 py-3"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  width: '22%',
                }}
              >
                Last active
              </th>
              <th
                className="text-right px-4 py-3"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  width: '22%',
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((row, idx) => (
              <ChurnRiskTableRow
                key={row.account_id}
                row={row}
                isLast={idx === accounts.length - 1}
                onOpenAccount={onOpenAccount}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────────────────────── footer ───────────────────────── */

function DrillDownFooter({
  state,
  onClose,
}: {
  state: FetchState;
  onClose: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 flex-wrap"
      style={{
        borderTop: `1px solid ${C.border}`,
        background: C.surface2,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 0.5,
        }}
      >
        {state.kind === 'ready' ? (
          state.data.truncated ? (
            <>
              Showing first {formatCount(state.data.limit)} of{' '}
              {formatCount(state.data.accounts.length)}+ — server cap hit.
            </>
          ) : (
            <>
              {formatCount(state.data.accounts.length)}{' '}
              {state.data.accounts.length === 1 ? 'account' : 'accounts'} at risk
            </>
          )
        ) : (
          <>&nbsp;</>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '8px 14px',
          minHeight: 40,
          cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  );
}

/* ───────────────────────── desktop row ───────────────────────── */

function ChurnRiskTableRow({
  row,
  isLast,
  onOpenAccount,
}: {
  row: AdminChurnRiskAccount;
  isLast: boolean;
  onOpenAccount: (accountId: string) => void;
}): JSX.Element {
  const C = useC();
  return (
    <tr style={{ borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}` }}>
      <td className="px-4 py-3" style={{ verticalAlign: 'top' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
            }}
          >
            {row.name}
          </div>
          <Pill tone={statusTone(row.status)}>{row.status}</Pill>
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 4,
          }}
        >
          {row.account_id}
        </div>
      </td>
      <td className="px-4 py-3" style={{ verticalAlign: 'top' }}>
        <OwnerEmailLink email={row.owner_email} />
      </td>
      <td className="px-4 py-3" style={{ verticalAlign: 'top' }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: row.last_active_at === null ? C.dim : C.body,
            whiteSpace: 'nowrap',
          }}
        >
          {formatLastActive(row.last_active_at)}
        </div>
        <div style={{ marginTop: 4 }}>
          <SilentBadge days={row.days_since_active} />
        </div>
      </td>
      <td className="px-4 py-3 text-right" style={{ verticalAlign: 'top' }}>
        <button
          type="button"
          onClick={() => onOpenAccount(row.account_id)}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '8px 12px',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            minHeight: 40,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Open account <ChevronRight size={13} />
        </button>
      </td>
    </tr>
  );
}

/* ───────────────────────── mobile card ───────────────────────── */

function ChurnRiskMobileCard({
  row,
  onOpenAccount,
}: {
  row: AdminChurnRiskAccount;
  onOpenAccount: (accountId: string) => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              fontWeight: 500,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {row.name}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              marginTop: 2,
            }}
          >
            {row.account_id}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Pill tone={statusTone(row.status)}>{row.status}</Pill>
          <SilentBadge days={row.days_since_active} />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
          }}
        >
          Owner
        </span>
        <OwnerEmailLink email={row.owner_email} />
      </div>

      <div className="flex flex-col gap-1">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
          }}
        >
          Last active
        </span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: row.last_active_at === null ? C.dim : C.body,
          }}
        >
          {formatLastActive(row.last_active_at)}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onOpenAccount(row.account_id)}
        className="w-full inline-flex items-center justify-center gap-1.5"
        style={{
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '10px 12px',
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          minHeight: 44,
          cursor: 'pointer',
        }}
      >
        Open account <ChevronRight size={14} />
      </button>
    </div>
  );
}

/* ───────────────────────── shared row bits ───────────────────────── */

function OwnerEmailLink({ email }: { email: string | null }): JSX.Element {
  const C = useC();
  if (email === null) {
    return (
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.dim,
        }}
      >
        —
      </span>
    );
  }
  return (
    <a
      href={`mailto:${email}`}
      className="inline-flex items-center gap-1.5"
      style={{
        fontFamily: monoFont,
        fontSize: 11,
        color: C.skyFg,
        textDecoration: 'underline',
        wordBreak: 'break-all',
      }}
      title={`Email ${email}`}
    >
      <Mail size={11} aria-hidden="true" />
      <span>{email}</span>
      <ExternalLink size={10} aria-hidden="true" />
    </a>
  );
}

function SilentBadge({ days }: { days: number | null }): JSX.Element {
  const C = useC();
  if (days === null) {
    return (
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.dim,
        }}
      >
        —
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-1.5"
      style={{
        background: C.amberDim,
        color: C.amberFg,
        border: `1px solid rgba(255,182,39,0.3)`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 0.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        paddingTop: 2,
        paddingBottom: 2,
      }}
    >
      {days}d silent
    </span>
  );
}
